import React, { useEffect, memo, useCallback, useState } from "react";
import { Box } from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputBase,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { sdmTableStyles } from "../SDMComponents/sdmTableStyles";

const DEFAULT_WINDOW_SIZE = 5;

const labelCellSx = {
  ...sdmTableStyles.headerCell,
  textTransform: "none",
  fontSize: "12px",
  fontWeight: 500,
  color: "#374151",
  verticalAlign: "top",
  paddingTop: "10px",
  width: "280px",
  whiteSpace: "normal",
  lineHeight: "1.5",
  borderRight: "1px solid #E2E8F0",
  backgroundColor: "#F8FAFC",
};

const sectionCellSx = {
  backgroundColor: "#EFF6FF",
  fontWeight: 700,
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#1E40AF",
  padding: "8px 12px",
  borderBottom: "1px solid #BFDBFE",
};

const calculatedFieldSx = {
  display: "block",
  width: "100%",
  padding: "14px 8px",
  fontSize: "13px",
  lineHeight: "1.5",
  border: "1px solid #E2E8F0",
  borderRadius: "4px",
  backgroundColor: "#F8FAFC",
  color: "#111827",
  fontWeight: 700,
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  cursor: "default",
  boxSizing: "border-box",
};

const inputBaseSx = {
  display: "block",
  width: "100%",
  padding: "6px 8px",
  fontSize: "13px",
  lineHeight: "1.5",
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  boxSizing: "border-box",
  "& input": {
    padding: 0,
    "&:focus": { outline: "none" },
  },
};

const dataCellSx = {
  ...sdmTableStyles.dataCell,
  verticalAlign: "middle",
  width: "240px",
};

const headerDataCellSx = {
  ...sdmTableStyles.headerCell,
  width: "240px",
};

const headerLabelCellSx = {
  ...sdmTableStyles.headerCell,
  width: "280px",
  textTransform: "none",
  fontSize: "12px",
};

const DynamicColumnsInputCell = memo(({ value, colIndex, field, onItemChange }) => (
  <InputBase
  multiline
  minRows={3}
  maxRows={10}
    value={value}
    onChange={(e) => onItemChange(colIndex, field, e.target.value)}
    size="small"
    fullWidth
    sx={{
      ...inputBaseSx,
      border: "1px solid #E2E8F0",
      borderRadius: "4px",
      backgroundColor: "#fff",
      "&:hover": { borderColor: "#CBD5E1" },
      "&:focus-within": {
        borderColor: "#2960EE",
        boxShadow: "0 0 0 3px rgba(41, 96, 238, 0.08)",
      },
    }}
  />
));

const DynamicColumnsCalculatedCell = memo(({ value }) => (
  <Box sx={calculatedFieldSx}>{value}</Box>
));

const DynamicColumnsSelectCell = memo(({ value, colIndex, field, options, onItemChange }) => (
  <Select
    value={value}
    onChange={(e) => onItemChange(colIndex, field, e.target.value)}
    size="small"
    fullWidth
    displayEmpty
    sx={{
      fontSize: "13px",
      backgroundColor: "#fff",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2960EE" },
      "& .MuiSelect-select": { padding: "6px 8px" },
    }}
  >
    <MenuItem value="" disabled sx={{ fontSize: "13px", color: "#94A3B8" }}>
      —
    </MenuItem>
    {options.map((opt) => (
      <MenuItem key={opt} value={opt} sx={{ fontSize: "13px", whiteSpace: "normal" }}>
        {opt}
      </MenuItem>
    ))}
  </Select>
));

const DynamicColumnsSelectWithInputCell = memo(({ value, colIndex, field, options, inputTrigger, onItemChange }) => {
  const parts = inputTrigger.split("____");
  const prefix = parts[0];
  const suffix = parts[1] ?? "";

  const isTriggered = value && value.startsWith(prefix) && value.endsWith(suffix);
  const selectedOption = isTriggered ? inputTrigger : (value || "");
  const inputValue = isTriggered && value !== inputTrigger
    ? value.slice(prefix.length, value.length - suffix.length)
    : "";

  const handleSelectChange = (e) => {
    const newOption = e.target.value;
    onItemChange(colIndex, field, newOption === inputTrigger ? inputTrigger : newOption);
  };

  const handleInputChange = (e) => {
    onItemChange(colIndex, field, prefix + e.target.value + suffix);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
      <Select
        value={selectedOption}
        onChange={handleSelectChange}
        size="small"
        displayEmpty
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "13px",
          backgroundColor: "#fff",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2960EE" },
          "& .MuiSelect-select": { padding: "6px 8px" },
        }}
      >
        <MenuItem value="" disabled sx={{ fontSize: "13px", color: "#94A3B8" }}>
          —
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt} value={opt} sx={{ fontSize: "13px", whiteSpace: "normal" }}>
            {opt}
          </MenuItem>
        ))}
      </Select>
      {selectedOption === inputTrigger && (
        <InputBase
          type="number"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="0"
          size="small"
          inputProps={{ style: { textAlign: "center", padding: 0 } }}
          sx={{
            width: "70px",
            flexShrink: 0,
            fontSize: "13px",
            border: "1px solid #E2E8F0",
            borderRadius: "4px",
            backgroundColor: "#fff",
            padding: "6px 8px",
            "&:hover": { borderColor: "#CBD5E1" },
            "&:focus-within": {
              borderColor: "#2960EE",
              boxShadow: "0 0 0 3px rgba(41, 96, 238, 0.08)",
            },
          }}
        />
      )}
    </Box>
  );
});

const DynamicColumnsTableComponent = ({
  title,
  columnLabel,
  rows,
  items = [],
  onItemChange,
  onAddColumn = () => {},
  onDeleteColumn = () => {},
  windowSize = DEFAULT_WINDOW_SIZE,
  getCalculatedValue,
}) => {
  const [offset, setOffset] = useState(0);

  const visibleItems = items.slice(offset, offset + windowSize);

  const getColLabel = useCallback(
    (index) => {
      if (typeof columnLabel === "function") return columnLabel(index);
      return `${columnLabel} ${index + 1}`;
    },
    [columnLabel]
  );

  useEffect(() => {
    if (offset > 0 && offset + windowSize > items.length) {
      setOffset(Math.max(0, items.length - windowSize));
    }
  }, [items.length, offset, windowSize]);

  const handleAddColumn = useCallback(() => {
    onAddColumn();
    const newLength = items.length + 1;
    setOffset(Math.max(0, newLength - windowSize));
  }, [onAddColumn, items.length, windowSize]);

  const handleDeleteColumn = useCallback(
    (colIndex) => onDeleteColumn(colIndex),
    [onDeleteColumn]
  );

  const renderCell = (row, colIndex) => {
    const item = items[colIndex] ?? {};

    if (row.calculated) {
      const value = getCalculatedValue ? getCalculatedValue(item, row) : "";
      return <DynamicColumnsCalculatedCell value={value} />;
    }

    const value = item[row.field] ?? "";

    if (row.type === "select" && row.options) {
      if (row.inputTrigger) {
        return (
          <DynamicColumnsSelectWithInputCell
            value={value}
            colIndex={colIndex}
            field={row.field}
            options={row.options}
            inputTrigger={row.inputTrigger}
            onItemChange={onItemChange}
          />
        );
      }
      return (
        <DynamicColumnsSelectCell
          value={value}
          colIndex={colIndex}
          field={row.field}
          options={row.options}
          onItemChange={onItemChange}
        />
      );
    }

    return (
      <DynamicColumnsInputCell
        value={value}
        colIndex={colIndex}
        field={row.field}
        onItemChange={onItemChange}
      />
    );
  };

  return (
    <Box sx={sdmTableStyles.tableWrapper}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          ...sdmTableStyles.tableContainer,
          overflowX: "auto",
          overflowY: "hidden",
          width: `${280 + visibleItems.length * 240}px`,
        }}
      >
        <Table
          size="small"
          sx={{ tableLayout: "fixed", width: `${280 + visibleItems.length * 240}px` }}
        >
          <colgroup>
            <col style={{ width: "280px" }} />
            {visibleItems.map((_, i) => (
              <col key={i} style={{ width: "240px" }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow sx={sdmTableStyles.headerRow}>
              <TableCell sx={headerLabelCellSx}>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "baseline" }}>
                  <Box>{title}</Box>
                  {items.length > 0 && (
                    <Box sx={{ fontSize: "11px", color: "#94A3B8", fontWeight: 400, whiteSpace: "nowrap" }}>
                      {offset + 1}–{Math.min(offset + windowSize, items.length)} из {items.length}
                    </Box>
                  )}
                </Box>
              </TableCell>
              {visibleItems.map((_, visibleIndex) => {
                const index = offset + visibleIndex;
                return (
                  <TableCell key={index} sx={headerDataCellSx}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {visibleIndex === 0 && items.length > windowSize && (
                        <IconButton
                          size="small"
                          disabled={offset === 0}
                          onClick={() => setOffset((v) => v - 1)}
                        >
                          ←
                        </IconButton>
                      )}
                      <Box sx={{ flex: 1, textAlign: "center", fontWeight: 600 }}>
                        {getColLabel(index)}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteColumn(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      {visibleIndex === visibleItems.length - 1 &&
                        items.length > windowSize && (
                          <IconButton
                            size="small"
                            disabled={offset + windowSize >= items.length}
                            onClick={() => setOffset((v) => v + 1)}
                          >
                            →
                          </IconButton>
                        )}
                      {visibleIndex === visibleItems.length - 1 && (
                        <IconButton
                          size="small"
                          onClick={handleAddColumn}
                          sx={{ color: "#2960EE" }}
                        >
                          <AddCircleIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIdx) => {
              if (row.type === "section") {
                return (
                  <TableRow key={rowIdx}>
                    <TableCell colSpan={visibleItems.length + 2} sx={sectionCellSx}>
                      {row.label}
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                <TableRow key={rowIdx} sx={sdmTableStyles.dataRow}>
                  <TableCell
                    sx={{
                      ...labelCellSx,
                      width: "280px",
                      fontWeight: row.bold ? 700 : 500,
                    }}
                  >
                    {row.label}
                  </TableCell>
                  {visibleItems.map((_, visibleIndex) => {
                    const colIdx = offset + visibleIndex;
                    return (
                      <TableCell key={colIdx} sx={dataCellSx}>
                        {renderCell(row, colIdx)}
                      </TableCell>
                    );
                  })}
                  <TableCell sx={sdmTableStyles.dataCell} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const DynamicColumnsTable = memo(DynamicColumnsTableComponent);

export default DynamicColumnsTable;
